/**
 * Serialized-context budget — mirrors the API's MAX_CONTEXT_CHARS. Enforced
 * client-side so a runaway page getter (a huge grid snapshot, a pathological
 * diagnostics buffer) degrades to a truncated context instead of a 400 —
 * a report must ALWAYS go through.
 */
export const MAX_CONTEXT_CHARS = 200_000;

/** Replaces a context block that pushed the payload over budget. */
export const DROPPED_BLOCK_MARKER =
  "<dropped: context exceeded the size budget>";

/**
 * Serialized length the way the API measures it: python's ``json.dumps``
 * separates with ", " / ": " — one character wider per structural comma and
 * colon than JSON.stringify's compact output. Counted exactly, so the client
 * budget can never disagree with the server's. (The remaining divergence —
 * UTF-16 units here vs code points there — only ever overcounts client-side,
 * which errs safe.)
 */
function pythonSerializedLength(value: unknown): number {
  let json: string | undefined;
  try {
    json = JSON.stringify(value);
  } catch {
    // Unserializable (a circular ref / BigInt that bypassed capture
    // sanitization): size cannot be measured — report 0 so the budget fit
    // passes the value through; the pane and POST have their own guards.
    return 0;
  }
  if (json === undefined) return 0;
  let extra = 0;
  let inString = false;
  for (let i = 0; i < json.length; i++) {
    const char = json[i];
    if (inString) {
      if (char === "\\") i++;
      else if (char === '"') inString = false;
    } else if (char === '"') inString = true;
    else if (char === "," || char === ":") extra++;
  }
  return json.length + extra;
}

/**
 * Fit the merged context under {@link MAX_CONTEXT_CHARS}: when over budget,
 * replace the LARGEST top-level blocks with a short marker (biggest first)
 * until the payload fits, and stamp `truncated: true` so triage can see the
 * capture was clipped. Under-budget contexts pass through untouched.
 */
export function fitContextToBudget(
  context: Record<string, unknown>
): Record<string, unknown> {
  if (pythonSerializedLength(context) <= MAX_CONTEXT_CHARS) return context;
  const fitted: Record<string, unknown> = { ...context, truncated: true };
  const largestFirst = Object.keys(context).sort(
    (a, b) =>
      pythonSerializedLength(context[b]) - pythonSerializedLength(context[a])
  );
  for (const key of largestFirst) {
    if (pythonSerializedLength(fitted) <= MAX_CONTEXT_CHARS) break;
    fitted[key] = DROPPED_BLOCK_MARKER;
  }
  return fitted;
}
